import { useEffect, useRef, useState } from 'react';
import { HEADLINE_FADE_MS, HEADLINE_INTERVAL_MS, LOCALES, type Locale } from '../i18n/locales';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

interface HeadlineRotatorProps {
  onLocaleChange?: (locale: Locale) => void;
}

type Phase = 'idle' | 'out' | 'in-start';

export function HeadlineRotator({ onLocaleChange }: HeadlineRotatorProps): JSX.Element {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('idle');
  const phraseRef = useRef<HTMLSpanElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const current = LOCALES[index]!;

  useEffect(() => {
    document.documentElement.lang = current.code;
    onLocaleChange?.(current);
  }, [current, onLocaleChange]);

  useEffect(() => {
    if (reducedMotion) {
      const id = window.setInterval(() => {
        setIndex((i) => (i + 1) % LOCALES.length);
      }, HEADLINE_INTERVAL_MS);
      return () => window.clearInterval(id);
    }

    const id = window.setInterval(() => {
      setPhase('out');
      window.setTimeout(() => {
        setIndex((i) => (i + 1) % LOCALES.length);
        setPhase('in-start');
        // force layout, then drop the entry class on the next frame
        if (phraseRef.current) {
          void phraseRef.current.offsetHeight;
        }
        requestAnimationFrame(() => setPhase('idle'));
      }, HEADLINE_FADE_MS);
    }, HEADLINE_INTERVAL_MS);

    return () => window.clearInterval(id);
  }, [reducedMotion]);

  const phraseClass =
    'headline__phrase' +
    (phase === 'out' ? ' is-out' : '') +
    (phase === 'in-start' ? ' is-in-start' : '');

  return (
    <>
      <div className="eyebrow">
        <span className="eyebrow__bar" aria-hidden="true" />
        <span>{current.eyebrow}</span>
      </div>

      <h1 className="headline" data-lang={current.code} lang={current.code}>
        <span ref={phraseRef} className={phraseClass}>
          <span className="headline__line1">{current.l1}</span>
          <br />
          <span className="headline__line2">{current.l2}</span>
        </span>
      </h1>

      <div className="lang-mini" aria-live="polite">
        <span>LANGUAGE</span>
        <span className="lang-mini__chip">{current.label.toUpperCase()}</span>
      </div>
    </>
  );
}
